import { useState, useEffect } from "react"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Eye, Image, Sliders } from "lucide-react"
import type { APISession } from '@/types/session'
import type { AmendmentDetectionSettings } from '@/api/admin-settings-api'
import type { DataSource } from '@/hooks/admin/useDatasourceState'

interface AmendmentSettingsPanelProps {
  session: APISession
  selectedDataSource: DataSource
  amendmentSettings: AmendmentDetectionSettings
  isMutating: boolean
  onAmendmentSettingChange: (session: APISession, setting: string, value: boolean | number) => void
  onNumericInputChange: (session: APISession, setting: 'dpi' | 'images_per_page', value: number) => void
}

export function AmendmentSettingsPanel({
  session,
  amendmentSettings,
  isMutating,
  onAmendmentSettingChange,
  onNumericInputChange
}: AmendmentSettingsPanelProps) {
  // Local state for immediate visual feedback
  const [localValues, setLocalValues] = useState({
    dpi: amendmentSettings.dpi,
    images_per_page: amendmentSettings.images_per_page
  })

  // Sync local state when amendment settings change
  useEffect(() => {
    setLocalValues({
      dpi: amendmentSettings.dpi,
      images_per_page: amendmentSettings.images_per_page
    })
  }, [amendmentSettings.dpi, amendmentSettings.images_per_page])

  const handleInputChange = (setting: 'dpi' | 'images_per_page', value: number) => {
    // Update local state immediately for responsive UI
    setLocalValues(prev => ({ ...prev, [setting]: value }))
    // Trigger debounced save
    onNumericInputChange(session, setting, value)
  }
  return (
    <div className="border-t bg-slate-50/50 dark:bg-slate-800/50 p-4 space-y-4">
      {/* Processing options */}
      <div className="space-y-4">
        {/* Basic Processing Options */}
        <div>
          <div className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
            <Eye className="h-3 w-3" />
            Processing Options
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id={`process-new-bills-${session}`}
                checked={amendmentSettings.process_new_bills}
                onCheckedChange={(enabled) => onAmendmentSettingChange(session, 'process_new_bills', enabled)}
                disabled={isMutating}
                className="scale-75"
              />
              <Label htmlFor={`process-new-bills-${session}`} className="text-xs">
                Process New Bills
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id={`process-version-changes-${session}`}
                checked={amendmentSettings.process_version_changes}
                onCheckedChange={(enabled) => onAmendmentSettingChange(session, 'process_version_changes', enabled)}
                disabled={isMutating}
                className="scale-75"
              />
              <Label htmlFor={`process-version-changes-${session}`} className="text-xs">
                Process Version Changes
              </Label>
            </div>
          </div>
        </div>

        {/* PDF Processing Settings */}
        <div>
          <div className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
            <Image className="h-3 w-3" />
            PDF Processing Settings
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* DPI Setting */}
            <div className="space-y-2">
              <Label htmlFor={`dpi-${session}`} className="text-xs">
                DPI (Image Quality)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id={`dpi-${session}`}
                  type="number"
                  min="100"
                  max="600"
                  step="50"
                  value={localValues.dpi}
                  onChange={(e) => handleInputChange('dpi', parseInt(e.target.value) || 200)}
                  disabled={isMutating}
                  className="h-8 text-xs"
                />
                <span className="text-xs text-muted-foreground">px</span>
              </div>
              <div className="text-xs text-muted-foreground">
                Higher DPI = better quality, higher cost
              </div>
            </div>

            {/* Images per Page */}
            <div className="space-y-2">
              <Label htmlFor={`images-per-page-${session}`} className="text-xs">
                Images per Page
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id={`images-per-page-${session}`}
                  type="number"
                  min="1"
                  max="4"
                  value={localValues.images_per_page}
                  onChange={(e) => handleInputChange('images_per_page', parseInt(e.target.value) || 2)}
                  disabled={isMutating}
                  className="h-8 text-xs"
                />
                <span className="text-xs text-muted-foreground">splits</span>
              </div>
              <div className="text-xs text-muted-foreground">
                More splits = better detection, higher cost
              </div>
            </div>
          </div>
        </div>

        {/* Cost Estimation */}
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200 text-xs">
            <Sliders className="h-3 w-3" />
            <span className="font-medium">Cost Impact:</span>
            <span>
              DPI {localValues.dpi} × {localValues.images_per_page} image{localValues.images_per_page > 1 ? 's' : ''} per page
            </span>
          </div>
          <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
            Higher settings improve accuracy but increase processing cost and time
          </div>
        </div>
      </div>
    </div>
  )
}
